<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script>
  import { find, map } from 'lodash'
  import { insertEmoji } from '@/helper'
  import VEmojiPicker from 'v-emoji-picker'
  import { mapFields } from 'vuex-map-fields'
  import { mapActions, mapState } from 'vuex'
  import { isMobile } from 'mobile-device-detect'
  import FallbackAvatar from '@/components/ui/FallbackAvatar'
  import AttachmentItem from '@/components/overlays/Chat/AttachmentItem'

  export default {
    name: 'ChatConversation',
    components: {
      VEmojiPicker,
      FallbackAvatar,
      AttachmentItem,
    },
    props: {
      conversationId: String,
    },
    data() {
      return {
        file: null,
        newMessage: '',
        tmpMessage: '',
        pendingFile: null,
        pendingMessages: [],
        emojiOpen: false,
        timeout: null,
        error: false,
        isMobile: isMobile,
      }
    },
    computed: {
      ...mapFields('chat', [
        'conversation',
        'isOpen',
        'reloadUnread',
      ]),
      ...mapState('auth', [ 'user' ]),
    },
    watch: {
      isOpen(val) {
        if (val) {
          this.init()
        }
      },
      file(val) {
        if (val) {
          const reader = new FileReader()
          reader.onload = async () => { this.onload(val, reader) }
          reader.readAsDataURL(this.file)
        }
      },
    },
    beforeDestroy() {
      clearTimeout(this.timeout)
    },
    created() {
      this.init()
    },
    mounted() {
      // Have to call here scrollToEnd on mount too because updated will be called after 5seconds when the messages are marked as read and used $nextTick to make sure entire view is been rendered
      this.$nextTick(() => this.scrollToEnd())
    },
    updated() {
      this.$nextTick(() => this.scrollToEnd())
    },
    methods: {
      ...mapActions('chat', [ 'chat' ]),
      async getConversation() {
        const chat = await this.chat()
        const historyRequest = chat.history(this.conversationId)
        historyRequest && historyRequest.then(({ data }) => {
          this.conversation = data
        })
      },
      init() {
        if (!this.conversation || this.conversation.id !== this.conversationId) {
          this.conversation = null
          this.getConversation()
        }
        this.scrollToEnd()
        this.timeout = setTimeout(this.markAsRead, 5000)
      },
      selectEmoji(emoji) {
        insertEmoji(emoji, this, 'newMessage')
      },
      inputHandler(e) {
        if (e.keyCode === 13 && !e.shiftKey) {
          e.preventDefault()
          this.submitMessage()
        }
      },
      async submitMessage() {
        this.error = false
        if (this.newMessage) {
          this.tmpMessage = this.newMessage
          this.newMessage = ''
          const that = this
          const chat = await that.chat()

          chat.sendMessage(
            that.conversation.id,
            that.tmpMessage,
            map(that.conversation.participants, 'id'),
          ).then(_ => {
            that.pendingMessages = []
            that.tmpMessage = ''
            that.$emit('showStatusMessage')
          }).catch(function(error) {
            that.pendingMessages = []
            that.error = true
            that.newMessage = that.tmpMessage
          })
          this.markAsRead()
        }
      },
      async uploadFile(data) {
        if (data) {
          const that = this
          const chat = await that.chat()
          chat.uploadFile(
            that.conversation.id,
            data,
            map(that.conversation.participants, 'id'),
          ).then(function(response) {
            that.error = false
            that.$emit('showStatusMessage')
          }).catch(function(error) {
            that.error = true
          }).finally(function() {
            that.file = null
            that.pendingFile = null
            that.markAsRead()
          })
        }
      },
      async markAsRead() {
        if (this.conversation) {
          const chat = await this.chat()
          chat.markAsRead(
            this.conversation.id,
            map(this.conversation.participants, 'id'),
          ).then(async _ => {
            await this.getConversation()
            this.reloadUnread = true
          })
        }
      },
      isOwnMessage(message) {
        if (this.user) {
          return message.sender === this.user.id
        }
      },
      getSenderName(id) {
        const sender = find(this.conversation.participants, { id: id })
        if (!sender || (!sender.first_name && !sender.last_name)) {
          return id
        }
        return this.displayUserName(sender)
      },
      scrollToEnd() {
        const content = this.$refs.messagesContainer
        if (content) {
          const messages = document.querySelectorAll('.speech-bubble')
          const lastMessage = messages.length && messages[messages.length - 1]
          lastMessage && lastMessage.scrollIntoView()
        }
      },
      closeChat() {
        this.$emit('closeChat')
        this.emojiOpen = false
      },
      async onload(val, reader) {
        const fileData = { type: val.type || '', name: val.name || '', content: reader.result }
        this.pendingFile = fileData
        await this.uploadFile(fileData)
      },
    },
  }
</script>
